import {
  ButtonProps,
  createTheme,
  MantineColorScheme,
  MantineProvider,
  MantineTheme,
  useMantineColorScheme,
} from '@mantine/core';
import { PropsWithChildren, useEffect } from 'react';

import { themeConfig } from '@mygpt/app/theme/config';

const theme = createTheme({
  ...themeConfig,
  breakpoints: {
    xs: '30em',
    sm: '48em',
    md: '64em',
    lg: '74em',
    xl: '90em',
  },
  primaryColor: 'violet',
  defaultRadius: 8,
  components: {
    Button: {
      // Subscribe to theme and component params
      styles: (theme: MantineTheme, params: ButtonProps) => ({
        root: {
          fontWeight: 400,
          transitionProperty: 'all',
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
          transitionDuration: '150ms',
        },
      }),
    },
    ActionIcon: {
      styles: (theme: MantineTheme, params: { variant: string }) => ({
        root: {
          backgroundColor: params.variant === 'light' ? theme.colors['violet'][2] : undefined,

          '&:hover': {
            backgroundColor: params.variant === 'light' ? theme.colors['violet'][3] : undefined,
          },
        },
      }),
    },
    Input: {
      styles(theme: MantineTheme) {
        return {
          input: {
            // 设置variant为"default"时的样式
            defaultProps: {
              style: {
                borderColor: '#000',

                '&::placeholder': {
                  color: theme.colors['dark'][4],
                },
                '&:disabled': {
                  borderColor: theme.colors['dark'][2],
                  backgroundColor: theme.white,
                  color: theme.colors['dark'][3],
                },
              },
            },
          },
        };
      },
    },
    TagsInput: {
      styles(theme: MantineTheme) {
        return {
          input: {
            borderColor: '#E4E8FF',
            '&:focus': {
              borderColor: theme.colors[theme.primaryColor][5],
            },
            '&::placeholder': {
              color: theme.colors['dark'][4],
            },
            '&:disabled': {
              borderColor: theme.colors['dark'][2],
              backgroundColor: theme.white,
              color: theme.colors['dark'][3],
            },
          },
          defaultValue: {
            backgroundColor: theme.colors[theme.primaryColor][1],
            color: theme.colors[theme.primaryColor][7],
          },
        };
      },
    },
    HoverCard: {
      styles() {
        return {
          dropdown: {
            borderRadius: '16px',
          },
        };
      },
    },
    Card: {
      styles() {
        return {
          root: {
            borderRadius: '16px',
          },
        };
      },
    },
    Modal: {
      styles() {
        return {
          modal: {
            borderRadius: 12,
          },
        };
      },
    },
    TextInput: {
      styles(theme: MantineTheme, params: Record<string, unknown>) {
        return {
          input: {
            borderColor: params['error'] ? null : '#E4E8FF',
          },
          error: {
            color: theme.colors['red'][6],
          },
          invalid: {
            color: theme.colors['red'][6],
          },
        };
      },
    },
    Textarea: {
      styles(theme: MantineTheme) {
        return {
          input: {
            borderColor: '#E4E8FF',
            '&:disabled': {
              backgroundColor: 'rgba(255,255,255,0.5)',
            },
          },
        };
      },
    },
    Select: {
      styles(theme: MantineTheme) {
        return {
          input: {
            borderColor: '#E4E8FF',

            '&:disabled': {
              borderColor: '#E4E8FF',
              color: '#333',
            },
          },
        };
      },
    },
    DatePickerInput: {
      styles(theme: MantineTheme) {
        return {
          input: {
            borderColor: '#E4E8FF',

            '&:disabled': {
              borderColor: '#E4E8FF',
              color: '#333',
            },
          },
        };
      },
    },
    MultiSelect: {
      styles(theme: MantineTheme) {
        return {
          input: {
            borderColor: '#E4E8FF',

            '&:disabled': {
              borderColor: '#E4E8FF',
              color: '#333',
            },
          },
        };
      },
    },
    Dialog: {
      styles(theme: MantineTheme) {
        return {
          closeButton: {
            color: theme.colors['gray'],
          },
        };
      },
    },
    Checkbox: {
      styles(theme: MantineTheme) {
        return {
          input: {
            borderColor: theme.colors['gray'][2],
            '&:checked': {
              backgroundColor: theme.colors[theme.primaryColor][5],
              borderColor: theme.colors[theme.primaryColor][5],
            },
          },
        };
      },
    },
    Table: {
      styles(theme: MantineTheme) {
        return {
          table: {
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow: '0px 1px 4.5px 0px rgba(228, 228, 228, 0.20)',
          },
          thead: {
            backgroundColor: theme.colors[theme.primaryColor][1],
          },
          tbody: {
            backgroundColor: '#FFF',
          },
          tr: {
            borderBottomColor: theme.colors['gray'][1],

            '&:hover': {
              backgroundColor: `${theme.colors[theme.primaryColor][0]} !important`,
            },
          },
          th: {
            fontWeight: 400,
            color: theme.colors['gray'][5],
          },
          td: {
            paddingTop: 15,
            paddingBottom: 15,
            color: theme.colors['gray'][7],
          },
          caption: {
            borderTop: `1px solid ${theme.colors['gray'][2]}`,
            marginTop: 0,
          },
        };
      },
    },
    JsonInput: {
      styles(theme: MantineTheme, params: Record<string, unknown>) {
        return {
          input: {
            borderColor: params['error'] ? null : '#E4E8FF',
          },
          error: {
            color: theme.colors['red'][6],
          },
          invalid: {
            color: theme.colors['red'][6],
          },
        };
      },
    },
    PasswordInput: {
      styles(theme: MantineTheme, params: Record<string, unknown>) {
        return {
          input: {
            borderColor: params['error'] ? null : '#E4E8FF',
          },
          error: {
            color: theme.colors['red'][6],
          },
          invalid: {
            color: theme.colors['red'][6],
          },
        };
      },
    },
  },
  other: {
    ...(themeConfig.other ?? ({} as never)),
  },
});

export const ThemeProvider = ({ children }: PropsWithChildren<{ id?: string; theme?: MantineColorScheme }>) => {
  return <MantineProvider theme={theme}>{children}</MantineProvider>;
};

export const AuthChangeTheme = ({ theme }: PropsWithChildren<{ theme: MantineColorScheme }>) => {
  const { setColorScheme } = useMantineColorScheme();

  useEffect(() => {
    setColorScheme(theme);
  }, [theme]);

  return null;
};
